import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import LiveTrackingView from "./LiveTrackingView";
import NextVesselCountdown from "./NextVesselCountdown";
import WeatherWidget from "./WeatherWidget";
import RBVesselAnalytics from "./RBVesselAnalytics";
import CostPerTonChart from "./CostPerTonChart";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Boat, Plus, Warning, ChartBar, Gauge as GaugeIcon, CurrencyDollar } from "@phosphor-icons/react";

/**
 * RBVesselBranch — the RB Vessel Performance branch of the Grindrod RB Management Portal.
 *  Renders everything that used to live directly on the dashboard:
 *   - Next-scheduled vessel countdown
 *   - Weather widget
 *   - Pending-approvals nudge
 *   - Active vessel switcher + LiveTrackingView
 *  Designed to be embedded inside the Portal's tab system (no <Header /> here —
 *  the Portal provides it). Also used standalone at /dashboard for legacy routes.
 */
export default function RBVesselBranch() {
    const { user } = useAuth();
    const canSeeAnalytics = user && (user.role === "admin" || user.role === "manager");
    const [view, setView] = useState("live"); // 'live' | 'analytics'
    const [vessels, setVessels] = useState([]);
    const [selectedId, setSelectedId] = useState(null);
    const [pendingCount, setPendingCount] = useState(0);
    const [loading, setLoading] = useState(true);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/vessels", { params: { status: "active" } });
            setVessels(data);
            setSelectedId((prev) => prev || (data.length > 0 ? data[0].id : null));
            if (user && user.role !== "supervisor") {
                try {
                    const { data: pu } = await api.get("/users", { params: { status: "pending" } });
                    setPendingCount(pu.length);
                } catch {
                    /* ignore if not permitted */
                }
            }
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const canStart = user && user.role === "supervisor";

    return (
        <>
            {/* View toggle: Live Operations vs Analytics — branch-scoped (Analytics admin/manager only) */}
            <div className="mb-5 inline-flex bg-slate-100 p-1 rounded-sm" data-testid="rb-view-toggle">
                <button
                    onClick={() => setView("live")}
                    className={`px-4 py-2 rounded-sm text-xs uppercase tracking-wide font-display font-bold inline-flex items-center gap-1.5 transition-colors ${
                        view === "live" ? "bg-[var(--grindrod-navy)] text-white" : "text-slate-600 hover:bg-white"
                    }`}
                    data-testid="rb-view-live"
                >
                    <GaugeIcon size={14} weight="fill" /> Live Operations
                </button>
                {canSeeAnalytics && (
                    <button
                        onClick={() => setView("analytics")}
                        className={`px-4 py-2 rounded-sm text-xs uppercase tracking-wide font-display font-bold inline-flex items-center gap-1.5 transition-colors ${
                            view === "analytics" ? "bg-[var(--grindrod-navy)] text-white" : "text-slate-600 hover:bg-white"
                        }`}
                        data-testid="rb-view-analytics"
                    >
                        <ChartBar size={14} weight="fill" /> Analytics
                    </button>
                )}
                <button
                    onClick={() => setView("costs")}
                    className={`px-4 py-2 rounded-sm text-xs uppercase tracking-wide font-display font-bold inline-flex items-center gap-1.5 transition-colors ${
                        view === "costs" ? "bg-[var(--grindrod-navy)] text-white" : "text-slate-600 hover:bg-white"
                    }`}
                    data-testid="rb-view-costs"
                >
                    <CurrencyDollar size={14} weight="fill" /> Costs · R/Ton
                </button>
            </div>

            {view === "analytics" && canSeeAnalytics ? (
                <RBVesselAnalytics />
            ) : view === "costs" ? (
                <CostPerTonChart />
            ) : (
                <>
                    {/* Next scheduled vessel countdown + weather — side-by-side on desktop */}
                    <div className="grid gap-4 md:grid-cols-3 mb-5" data-testid="dashboard-top-strip">
                        <div className="md:col-span-2">
                            <NextVesselCountdown />
                        </div>
                        <div>
                            <WeatherWidget />
                        </div>
                    </div>

            {/* Pending approvals nudge for managers/admin */}
            {user && user.role !== "supervisor" && pendingCount > 0 && (
                <div className="g-card p-3 mb-5 flex items-center gap-3" style={{ borderLeft: "4px solid var(--grindrod-red)" }} data-testid="approvals-banner">
                    <Warning size={20} className="text-[var(--grindrod-red)]" />
                    <div className="flex-1">
                        <span className="font-semibold text-[var(--grindrod-navy)]">
                            {pendingCount} registration{pendingCount > 1 ? "s" : ""} pending
                        </span>
                        <span className="text-sm text-slate-500"> — awaiting your approval.</span>
                    </div>
                    <Link to="/approvals" className="btn-navy !py-1.5 !px-3 text-xs" data-testid="go-approvals-btn">
                        Review
                    </Link>
                </div>
            )}

            {loading ? (
                <div className="text-slate-500" data-testid="vessels-loading">Loading live operations…</div>
            ) : vessels.length === 0 ? (
                <EmptyState canStart={canStart} role={user?.role} />
            ) : (
                <>
                    {vessels.length > 1 && (
                        <div className="mb-5 flex flex-wrap gap-2" data-testid="vessel-switcher">
                            {vessels.map((v) => (
                                <button
                                    key={v.id}
                                    onClick={() => setSelectedId(v.id)}
                                    className={`px-4 py-2 text-sm font-semibold border transition ${
                                        selectedId === v.id
                                            ? "bg-[var(--grindrod-navy)] text-white border-[var(--grindrod-navy)]"
                                            : "bg-white text-[var(--grindrod-navy)] border-slate-300 hover:border-[var(--grindrod-navy)]"
                                    }`}
                                    data-testid={`switch-${v.id}`}
                                >
                                    <Boat size={14} className="inline mr-1.5" />
                                    {v.vessel_name}
                                </button>
                            ))}
                        </div>
                    )}
                    {selectedId && <LiveTrackingView vesselId={selectedId} />}
                </>
            )}
                </>
            )}
        </>
    );
}

function EmptyState({ canStart, role }) {
    return (
        <div className="g-card p-12 text-center" data-testid="vessels-empty">
            <Boat size={64} weight="duotone" className="mx-auto text-slate-300 mb-4" />
            <div className="font-display font-black text-2xl text-[var(--grindrod-navy)]">
                No vessels currently loading
            </div>
            <p className="text-slate-500 text-sm mt-2 mb-6 max-w-md mx-auto">
                {role === "supervisor"
                    ? "No loading operation is in progress. Open the Vessel Line-up to start the next scheduled vessel when it arrives at berth."
                    : "You'll see the live tracking dashboard here as soon as a supervisor starts a vessel from the line-up."}
            </p>
            {canStart && (
                <Link to="/lineup" className="btn-primary inline-flex items-center gap-2" data-testid="empty-go-lineup-btn">
                    <Plus size={16} /> Open vessel line-up
                </Link>
            )}
        </div>
    );
}
